import {
  Box,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import UserImage from "../../../../../UserImage";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsUserProfile,
  selectProfile,
} from "../../../../../../redux/reducers/gallery";
import TextView from "../../../../../TextView";
import { colors } from "../../../../../../utils";
import { useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import FlagIcon from "@mui/icons-material/Flag";
import Edit from "@assets/images/editIcon.svg";
import {
  setReportInstance,
  setReportLocation,
  setReportModalOpen,
} from "../../../../../../redux/actions";
import { ASSETS } from "../../../../../../utils/config";
import { useParams } from "react-router-dom";
import ShareIcon from "@mui/icons-material/Share";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { PATH } from "../../../../../../utils/config";
import { getLoggedInUser } from "../../../../../../utils/helpers";

const LinkVaultPreview = ({
  showMenu = false,
  links,
  handleEditLinkVault,
  fullPage = false,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const profile = useSelector(selectProfile);
  const isUserProfile = useSelector(selectIsUserProfile);

  const linksToShow = links?.filter((link) => link.is_cta_enabled);

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    if (!getLoggedInUser()) {
      // Redirect to login page if user is not authenticated
      navigate(PATH.LOGIN);
      return;
    }

    dispatch(setReportModalOpen(true));
    dispatch(setReportInstance(id));
    dispatch(setReportLocation("USER"));
    handleCloseMenu();
  };

  const handleShare = async () => {
    try {
      const baseUrl = window.location.origin;
      const linkVaultUrl = `${baseUrl}/gallery/${id}?view=linkvault`;
      await navigator.clipboard.writeText(linkVaultUrl);

      toast.success("LinkVault URL copied!", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      handleCloseMenu();
    } catch (error) {
      console.error("Failed to copy URL:", error);
      toast.error("Failed to copy URL");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        background:
          !fullPage &&
          `
          linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 50%),
          conic-gradient(from 90deg at 49.13% 50%, #F5EEDE -41.73deg, #CCFEFF 37.8deg, #F7FED5 140.4deg, #FDCFFE 216deg, #F5EEDE 318.27deg, #CCFEFF 397.8deg)
        `,
        height: "100%",
        width: "100%",
        p: "32px 24px",
        borderRadius: "8px",
        border: !fullPage && "1px solid #E1E1E1",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        gap: "20px",
        overflowY: "auto",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <UserImage size={80} image={profile?.profile_image} />
      <TextView
        variant="title"
        style={{
          color: colors.primary,
          WebkitLineClamp: 2,
          textAlign: "center",
          maxWidth: "100%",
          flexShrink: 0,
        }}
      >
        {profile?.first_name} {profile?.last_name}
      </TextView>

      {showMenu && (
        <>
          <IconButton
            style={{
              color: "#123051",
              width: "34px",
              height: "34px",
              backgroundColor: "#FFFFFF",
              position: "absolute",
              top: "32px",
              right: "24px",
            }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <FaEllipsisV
              color="#565556"
              style={{ cursor: "pointer", width: "20px", height: "16px" }}
            />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            TransitionComponent={Fade}
            timeout={{ enter: 100, exit: 300 }}
            PaperProps={{
              style: {
                borderRadius: "8px",
                width: "100px",
                transition: "box-shadow 0.3s ease-in-out",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {isUserProfile && (
              <MenuItem
                onClick={() => {
                  handleEditLinkVault();
                  handleCloseMenu();
                }}
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <img
                  style={{ marginRight: "0.2rem" }}
                  src={Edit}
                  className="img-fluid"
                  alt="Edit"
                  width="15"
                  height="15"
                />
                Edit
              </MenuItem>
            )}

            {!isUserProfile && (
              <>
                <MenuItem
                  onClick={handleShare}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <ShareIcon
                    sx={{
                      marginRight: "0.2rem",
                      width: "18px",
                      height: "18px",
                      color: "#565556",
                    }}
                  />
                  Share
                </MenuItem>
                <MenuItem
                  onClick={handleReport}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <FlagIcon
                    sx={{
                      marginRight: "0.2rem",
                      width: "18px",
                      height: "18px",
                      color: "#BBBBBB",
                    }}
                  />
                  Report
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      )}

      {linksToShow?.length > 0 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {linksToShow.map((link, index) => (
            <Box
              key={index}
              onClick={() => link.link && window.open(link.link, "_blank")}
              sx={{
                width: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 6px 12px 12px",
                minHeight: "60px",
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #BBBBBB",
                cursor: link.link ? "pointer" : "default",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "12px",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={link.icon || ASSETS.website}
                  alt={link.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  textAlign: "left",
                  color: "#344054",
                  fontSize: "16px",
                  fontWeight: "500",
                  fontFamily: "DM Sans",
                  pl: 4, // Left padding to account for image space (12px + 24px + extra buffer)
                  pr: 1,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  hyphens: "auto",
                }}
              >
                {link.title || "Untitled"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LinkVaultPreview;
